/**
 * LessonLink - ユーザー管理モジュール（講師のみ）
 */
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../amplify/data/resource';
import { showMessage, validateNotificationEmail } from './utils';

type Client = ReturnType<typeof generateClient<Schema>>;
let client: Client;

function getClient() {
  if (!client) {
    client = generateClient<Schema>();
  }
  return client;
}

/**
 * ユーザー管理画面のイベントを初期化
 */
export function initUsers() {
  const inviteForm = document.getElementById('invite-form') as HTMLFormElement;
  const usersList = document.getElementById('users-list') as HTMLElement;

  // 招待フォーム送信
  inviteForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    const input = document.getElementById('invite-email') as HTMLInputElement;
    const email = input.value.trim();

    if (!email) {
      showMessage('メールアドレスを入力してください');
      return;
    }

    if (!validateNotificationEmail(email)) {
      showMessage('メールアドレスの形式が正しくありません');
      return;
    }

    try {
      const { data, errors } = await (getClient() as any).mutations.inviteUser({ email });
      if (errors && errors.length > 0) {
        showMessage(errors[0].message || '招待に失敗しました');
        return;
      }
      if (data && !data.success) {
        showMessage(data.message);
        return;
      }
      showMessage(data?.message || '招待しました', 'success');
      input.value = '';
      await loadUsers();
    } catch (error) {
      console.error('Invite user error:', error);
      showMessage('招待に失敗しました');
    }
  });

  // ユーザー削除（イベント委譲）
  usersList.addEventListener('click', async (e) => {
    const deleteBtn = (e.target as HTMLElement).closest('[data-delete-user-id]') as HTMLElement;
    if (!deleteBtn) return;

    const userId = deleteBtn.dataset.deleteUserId!;
    const userEmail = deleteBtn.dataset.deleteUserEmail!;
    const userName = deleteBtn.dataset.deleteUserName!;

    if (!confirm(`${userName}さんを削除しますか？`)) return;

    try {
      const { data, errors } = await (getClient() as any).mutations.deleteUserAccount({
        userId,
        email: userEmail,
      });
      if (errors && errors.length > 0) {
        showMessage(errors[0].message || '削除に失敗しました');
        return;
      }
      if (data && !data.success) {
        showMessage(data.message);
        return;
      }
      showMessage('ユーザーを削除しました', 'success');
      await loadUsers();
    } catch (error) {
      console.error('Delete user error:', error);
      showMessage('削除に失敗しました');
    }
  });
}

/**
 * ユーザー一覧を読み込んで表示
 */
export async function loadUsers() {
  const container = document.getElementById('users-list') as HTMLElement;
  const emptyMsg = document.getElementById('users-empty') as HTMLElement;

  try {
    const { data: users } = await getClient().models.User.list();
    if (!users || users.length === 0) {
      container.innerHTML = '';
      emptyMsg.classList.remove('hidden');
      return;
    }

    emptyMsg.classList.add('hidden');

    // 講師を先頭、生徒を後に
    const sorted = [...users].sort((a, b) => {
      if (a.role === 'instructor' && b.role !== 'instructor') return -1;
      if (a.role !== 'instructor' && b.role === 'instructor') return 1;
      return (a.displayName || '').localeCompare(b.displayName || '');
    });

    container.innerHTML = sorted
      .map((user) => {
        const roleLabel = user.role === 'instructor' ? '講師' : '生徒';
        const escaped = (user.email || '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
        const nameEscaped = (user.displayName || '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
        const isStudent = user.role === 'student';
        return `<li class="py-3">
          <div class="flex items-center justify-between">
            <div>
              <div class="font-medium">${nameEscaped} <span class="text-sm text-gray-500">${roleLabel}</span></div>
              <div class="text-sm text-gray-600">${escaped}</div>
            </div>
            ${isStudent ? `<button type="button"
              data-delete-user-id="${user.id}"
              data-delete-user-email="${escaped}"
              data-delete-user-name="${nameEscaped}"
              class="text-red-600 hover:text-red-800 text-sm"
              aria-label="${nameEscaped}を削除">削除</button>` : ''}
          </div>
        </li>`;
      })
      .join('');
  } catch (error) {
    console.error('Load users error:', error);
    showMessage('ユーザー一覧の読み込みに失敗しました');
  }
}
